import { makeAutoObservable, runInAction } from 'mobx'
import api from '@/services/api'
import type { MCPServer, MCPServerCreate, MCPTool, MCPToolCall } from '@/types'

export interface MCPServerDetail extends MCPServer {
  tools: MCPTool[]
}

export interface ToolCallResult {
  success: boolean
  server_id?: string
  tool_name?: string
  result?: any
  error?: string
}

export interface ServerHealth {
  healthy: boolean
  status_code?: number
  error?: string
  server_id: string
}

class MCPStore {
  servers: MCPServer[] = []
  selectedServer: MCPServerDetail | null = null
  serverHealth: Record<string, ServerHealth> = {}
  toolCallResults: ToolCallResult[] = []
  isLoading = false
  isRegistering = false
  isCallingTool = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async fetchServers() {
    this.isLoading = true
    this.error = null
    try {
      const servers = await api.getMCPServers()
      runInAction(() => {
        this.servers = servers
        this.isLoading = false
      })
      // Check health for all servers
      this.checkAllServerHealth()
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch MCP servers'
        this.isLoading = false
      })
    }
  }

  async fetchServer(serverId: string) {
    this.isLoading = true
    this.error = null
    try {
      const server = await api.getMCPServer(serverId)
      runInAction(() => {
        this.selectedServer = server as MCPServerDetail
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch MCP server'
        this.isLoading = false
      })
    }
  }

  async registerServer(data: MCPServerCreate) {
    this.isRegistering = true
    this.error = null
    try {
      const result = await api.registerMCPServer(data)
      runInAction(() => {
        // Add to servers list - result contains the registered server info
        this.servers.push({
          id: data.id,
          name: data.name,
          type: data.type as MCPServer['type'],
          url: data.url,
          is_active: true,
          tools_count: result.tools_count || 0,
          metadata: data.metadata,
        })
        this.isRegistering = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to register MCP server'
        this.isRegistering = false
      })
      throw error
    }
  }

  async unregisterServer(serverId: string) {
    this.isLoading = true
    this.error = null
    try {
      await api.unregisterMCPServer(serverId)
      runInAction(() => {
        this.servers = this.servers.filter(s => s.id !== serverId)
        if (this.selectedServer?.id === serverId) {
          this.selectedServer = null
        }
        delete this.serverHealth[serverId]
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to unregister MCP server'
        this.isLoading = false
      })
      throw error
    }
  }

  async checkServerHealth(serverId: string) {
    try {
      const health = await api.checkMCPServerHealth(serverId)
      runInAction(() => {
        this.serverHealth[serverId] = health
      })
      return health
    } catch (error: any) {
      runInAction(() => {
        this.serverHealth[serverId] = {
          healthy: false,
          error: error.message || 'Health check failed',
          server_id: serverId,
        }
      })
    }
  }

  async checkAllServerHealth() {
    const promises = this.servers.map(s => this.checkServerHealth(s.id))
    await Promise.all(promises)
  }

  async callTool(data: MCPToolCall): Promise<ToolCallResult> {
    this.isCallingTool = true
    this.error = null
    try {
      const result = await api.callMCPTool(data)
      runInAction(() => {
        this.toolCallResults.unshift(result)
        // Keep only last 50 results
        if (this.toolCallResults.length > 50) {
          this.toolCallResults = this.toolCallResults.slice(0, 50)
        }
        this.isCallingTool = false
      })
      return result
    } catch (error: any) {
      const errorResult: ToolCallResult = {
        success: false,
        server_id: data.server_id,
        tool_name: data.tool_name,
        error: error.response?.data?.detail || 'Failed to call tool',
      }
      runInAction(() => {
        this.toolCallResults.unshift(errorResult)
        this.isCallingTool = false
      })
      return errorResult
    }
  }

  async refreshServerTools(serverId: string) {
    this.isLoading = true
    try {
      const response = await fetch(`/api/v1/mcp/servers/${serverId}/refresh-tools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      })
      const result = await response.json()
      if (result.success) {
        // Refresh the server details
        await this.fetchServer(serverId)
      }
      runInAction(() => {
        this.isLoading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = 'Failed to refresh tools'
        this.isLoading = false
      })
    }
  }

  clearError() {
    this.error = null
  }

  clearToolResults() {
    this.toolCallResults = []
  }

  getServerById(id: string): MCPServer | undefined {
    return this.servers.find(s => s.id === id)
  }
}

export const mcpStore = new MCPStore()
export default mcpStore
